import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
  type ThemeOptions,
  outlinedInputClasses,
} from '@mui/material';
import { vars } from '@tgtg/kit';
import { type FC, type ReactNode } from 'react';
import CheckboxCheckedIcon from 'ui/SvgIcon/icons/CheckboxCheckedIcon';
import CheckboxIcon from 'ui/SvgIcon/icons/CheckboxIcon';

import { adyenOverrides } from './adyenOverrides';
import baseLayout, { themeWithBreakpointsIncludingDrawer } from './baseLayout';
import borderRadius from './borderRadius';
import borders from './borders';
import breakpoints from './breakpoints';
import elevation from './elevation';
import { fontfaces } from './fontfaces';
import { kitOverrides } from './kitOverrides';
import palette from './palette';
import shadow from './shadow';
import typography, { themeWithResponsiveTypography } from './typography';

const themeOptions: ThemeOptions = {
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiCssBaseline: {
      styleOverrides: (theme) => fontfaces + adyenOverrides(theme) + kitOverrides(theme),
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`&.Mui-error .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: theme.palette.cvi.red[40],
          },
        }),
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: '1.5rem',
        },
      },
    },
    MuiIcon: {
      defaultProps: {
        baseClassName: 'material-symbols-rounded',
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
      defaultProps: {
        variantMapping: {
          headingLarge: 'h1',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.active': {
            '& .MuiListItemText-root': {
              '& .MuiListItemText-primary': {
                fontWeight: 700,
              },
            },
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          '&:hover': {
            cursor: 'pointer',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-error': {
            color: theme.palette.field.error,
          },
        }),
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        icon: <CheckboxIcon />,
        checkedIcon: <CheckboxCheckedIcon />,
      },
      styleOverrides: {
        root: ({ theme: { spacing } }) => ({
          padding: spacing(0, 1, 0, 0),
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: vars.color.surface.negativeBold,
          color: vars.color.static.light,
          height: vars.size.size200,
          minWidth: vars.size.size200,
          fontSize: vars.typography.labels.xSmall.fontSize,
          padding: 0,
        },
      },
    },
  },
  elevation,
  palette,
  typography,
  borders,
  borderRadius,
  shadow,
  breakpoints,
  baseLayout,
};

// Using breakpoints defined in the theme to create responsive typography
export const theme = themeWithResponsiveTypography(themeOptions, createTheme(themeOptions));
export const themeIncludingDrawer = themeWithBreakpointsIncludingDrawer(
  themeOptions,
  createTheme(themeOptions),
);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: FC<ThemeProviderProps> = ({ children }) => (
  <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>
);
